import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '80px')};
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 16px;
`;

const BrandBadge = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #dc2626;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  padding: 10px 12px;
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  background: ${(p) => (p.$active ? '#dc2626' : 'transparent')};
  text-decoration: none;
  font-size: 14px;
  &:hover {
    background: ${(p) => (p.$active ? '#dc2626' : '#fee2e2')};
    color: ${(p) => (p.$active ? '#fff' : '#991b1b')};
  }
`;

const Footer = styled.div`
  border-top: 1px solid #e2e8f0;
  padding: 16px;
  color: #64748b;
  font-size: 12px;
`;

const IconBase = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  />
);

const DashboardIcon = () => (
  <IconBase>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="4" rx="1" />
    <rect x="14" y="10" width="7" height="11" rx="1" />
    <rect x="3" y="13" width="7" height="8" rx="1" />
  </IconBase>
);

const DuesIcon = () => (
  <IconBase>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <path d="M7 8h4" />
    <path d="M7 12h10" />
  </IconBase>
);

const ProfileIcon = () => (
  <IconBase>
    <circle cx="12" cy="9" r="3.2" />
    <path d="M6.5 19a5.5 5.5 0 0 1 11 0" />
  </IconBase>
);

const InquiryIcon = () => (
  <IconBase>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </IconBase>
);

export default function RecoverySidebar({ isOpen, currentSection }) {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  const navigation = useMemo(
    () => ([
      { name: 'Dashboard',         key: 'dashboard',         href: '/recovery/dashboard',         icon: <DashboardIcon /> },
      { name: 'Dues List',          key: 'dues',              href: '/recovery/dues',              icon: <DuesIcon /> },
      { name: 'Inactivated List Followup',  key: 'inquiry-followup',  href: '/recovery/inquiry-followup',  icon: <InquiryIcon /> },
      { name: 'Profile',            key: 'profile',           href: '/recovery/profile',           icon: <ProfileIcon /> },
    ]),
    []
  );

  const isActive = (href) => {
    if (!href || href === '#') return false;
    if (href === '/recovery/dashboard') return currentPath === href || currentSection === 'dashboard';
    return currentPath === href || currentSection === navigation.find((n) => n.href === href)?.key;
  };

  return (
    <Aside $open={isOpen}>
      <TopBar>
        {isOpen ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BrandBadge>RO</BrandBadge>
            <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Recovery</span>
          </div>
        ) : (
          <BrandBadge>RO</BrandBadge>
        )}
      </TopBar>
      <Nav>
        {navigation.map((item) => {
          const itemActive = isActive(item.href);
          return (
            <div key={item.key} style={{ marginBottom: 6 }}>
              <NavLink href={item.href} $active={itemActive}>
                <span>{item.icon}</span>
                {isOpen && <span>{item.name}</span>}
              </NavLink>
            </div>
          );
        })}
      </Nav>
      {isOpen && (
        <Footer>
          <div>
            <div style={{ fontWeight: 600 }}>Recovery Suite</div>
            <div style={{ marginTop: 4 }}>Version 1.0.0</div>
          </div>
        </Footer>
      )}
    </Aside>
  );
}
